import React from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Paper, Typography } from '@material-ui/core';
import { makeStyles, alpha } from '@material-ui/core/styles';
import { ArrowDropUp, ArrowDropDown } from '@material-ui/icons';
import clsx from 'clsx';
import { Skeleton } from '@material-ui/lab';


const getColor = theme => ({ color }) => (
  color === 'primary' ? theme.palette.primary.main
  : color === 'secondary' ? theme.palette.secondary.main
  : color === 'flora' ? theme.palette.flora.primary.main
  : color === 'fauna' ? theme.palette.fauna.primary.main
  : theme.palette.primary.main
);

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    backgroundColor: ({ variant }) => (
      variant === 'dark' ? theme.palette.common.black
      : variant === 'light' ? theme.palette.common.white
      : theme.palette.common.white
    ),
  },
  title: {
    fontSize: theme.typography.subtitle1.fontSize,
    color: ({ variant }) => (
      variant === 'light' ? theme.palette.common.gray600
      : variant === 'dark' ? theme.palette.common.lightGray
      : theme.palette.common.gray600
    ),
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  number: {
    marginLeft: theme.spacing(2),
    width: ({ isLoading }) => isLoading ? '30%' : '100%',
    fontSize: '3em',
    fontWeight: 400,
    color: ({ variant }) => (
      variant === 'light' ? theme.palette.common.gray700
      : variant === 'dark' ? theme.palette.common.white
      : theme.palette.common.black
    ),
  },
  icon: {
    color: getColor(theme),
  },
  iconBackground: {
    height: '40px',
    width: '40px',
    backgroundColor: ({ color }) => alpha(getColor(theme)({ color }), 0.15),
    borderRadius: '50%',
  },
  unit: {
    fontSize: '1.4em',
    fontWeight: 400,
    color: ({ variant }) => (
      variant === 'light' ? theme.palette.common.gray600
      : variant === 'dark' ? theme.palette.common.lightGray
      : theme.palette.common.gray600
    ),
    marginTop: theme.spacing(2.5),
    marginLeft: theme.spacing(1),
  },
  variation: {
    fontSize: '1.5em',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
  },
  variationPositive: {
    color: theme.palette.success.main,
  },
  variationNegative: {
    color: theme.palette.error.main,
  },
  variationIcon: {
    fontSize: '1.2em',
  },
}));

const IndicatorCard = props => {
  const {
    className,
    title = '',
    number = 0,
    color = 'primary',
    Icon = () => <></>,
    variant = 'light',
    isLoading,
    unit = '',
    variation,
  } = props;
  const classes = useStyles({ color, variant, isLoading });

  const variationValue = parseFloat(variation);
  const isPositive = variationValue > 0;
  const isNegative = variationValue < 0;
  const VariationIcon = isPositive ? ArrowDropUp : ArrowDropDown;
  return (
    <Paper className={clsx(classes.container, className)}>
      <Box display="flex" height="100%">
        <Box display="flex" flexDirection="column" width="100%">
          <Box mb={1}>
            <Typography className={classes.title} noWrap>{title}</Typography>
          </Box>
          <Divider />
          <Box display="flex" alignItems="center" height="100%">
            <Box display="flex" alignItems="baseline">
              <Typography variant="body1" className={classes.number}>
                {isLoading ? <Skeleton width={80} animation="wave" variant="text" /> : number}
              </Typography>
              {!isLoading && unit && (
                <Typography variant="body1"className={classes.unit}>
                  {unit}
                </Typography>
              )}
              {!isLoading && variation && !isNaN(variationValue) && (
                <span
                  className={clsx(
                    classes.variation,
                    isPositive && classes.variationPositive,
                    isNegative && classes.variationNegative,
                  )}
                  title="Con respecto al año anterior"
                >
                  (<VariationIcon variant="body1" className={classes.variationIcon} />{Math.abs(variationValue).toFixed(1)}%)
                </span>
              )}
            </Box>
            <Box flexGrow={1} />
            <Box p={1} display="flex" alignItems="center" justifyContent="center" className={classes.iconBackground}>
              <Icon className={classes.icon} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

IndicatorCard.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  number: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  variation: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  Icon: PropTypes.oneOfType([ PropTypes.object, PropTypes.func ]),
  color: PropTypes.string,
  variant: PropTypes.string,
  isLoading: PropTypes.bool,
  unit: PropTypes.string,
};


export { IndicatorCard };
